/**
 * Artifact Management Section - Owner Dashboard
 * Elvis Aloha Collection
 * 
 * Features:
 * - List all artifacts with filtering
 * - Create new artifacts
 * - Edit existing artifacts
 * - View financial data
 * - Manage images
 * - Set availability status
 * - Publish/unpublish
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Plus, Search, Filter, Edit, Eye, Trash2, DollarSign, Image, Archive, Check, X, ChevronRight } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ============================================================================
// CONSTANTS
// ============================================================================

const CATEGORIES = [
  { value: 'ticket', label: 'Ticket' },
  { value: 'design_mockup', label: 'Design Mock-up' },
  { value: 'production_material', label: 'Production Material' },
  { value: 'document', label: 'Document' },
  { value: 'photograph', label: 'Photograph' },
  { value: 'memorabilia', label: 'Memorabilia' },
  { value: 'costume', label: 'Costume' },
  { value: 'award', label: 'Award' },
  { value: 'certification', label: 'Certification' },
  { value: 'other', label: 'Other' }
];

const RELATIONSHIPS = [
  { value: 'aloha_direct', label: 'Aloha from Hawaii - Direct' },
  { value: 'aloha_related', label: 'Aloha from Hawaii - Related' },
  { value: 'elvis_general', label: 'Elvis Presley - General' },
  { value: 'historical_context', label: 'Historical Context' },
  { value: 'other', label: 'Other' }
];

const AVAILABILITY_STATUSES = [
  { value: 'available_private_sale', label: 'Available for Private Sale', color: '#22c55e' },
  { value: 'price_on_request', label: 'Price on Request', color: '#3b82f6' },
  { value: 'open_to_offers', label: 'Open to Offers', color: '#8b5cf6' },
  { value: 'not_currently_available', label: 'Not Currently Available', color: '#6b7280' },
  { value: 'permanent_collection', label: 'Permanent Collection', color: '#0ea5e9' },
  { value: 'consignment', label: 'Consignment', color: '#f59e0b' },
  { value: 'scheduled_for_auction', label: 'Scheduled for Auction', color: '#ec4899' },
  { value: 'at_auction', label: 'At Auction', color: '#ef4444' },
  { value: 'reserved', label: 'Reserved', color: '#f97316' },
  { value: 'sale_pending', label: 'Sale Pending', color: '#eab308' },
  { value: 'sold', label: 'Sold', color: '#10b981' },
  { value: 'withdrawn', label: 'Withdrawn', color: '#94a3b8' },
  { value: 'archived', label: 'Archived', color: '#475569' }
];

const PUBLICATION_STATUSES = [
  { value: 'draft', label: 'Draft', color: '#f59e0b' },
  { value: 'published', label: 'Published', color: '#22c55e' },
  { value: 'private', label: 'Private', color: '#6b7280' },
  { value: 'archived', label: 'Archived', color: '#94a3b8' }
];

// ============================================================================
// API HELPERS
// ============================================================================

const fetchWithAuth = async (url, options = {}) => {
  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  
  return response.json();
};

// ============================================================================
// ARTIFACTS LIST VIEW
// ============================================================================

export const ArtifactsListSection = () => {
  const navigate = useNavigate();
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  
  // Filters
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  const loadArtifacts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, per_page: 25 });
      if (search) params.append('search', search);
      if (categoryFilter) params.append('category', categoryFilter);
      if (statusFilter) params.append('pub_status', statusFilter);
      
      const data = await fetchWithAuth(`/api/collection-items/manage/list?${params}`);
      setArtifacts(data.artifacts || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, search, categoryFilter, statusFilter]);
  
  useEffect(() => {
    loadArtifacts();
  }, [loadArtifacts]);
  
  const handleDelete = async (id) => {
    if (!window.confirm('Archive this artifact? It will be hidden but not deleted.')) return;
    
    try {
      await fetchWithAuth(`/api/collection-items/manage/${id}`, { method: 'DELETE' });
      loadArtifacts();
    } catch (err) {
      alert('Failed to archive: ' + err.message);
    }
  };
  
  const handlePublish = async (id, isPublished) => {
    try {
      const endpoint = isPublished ? 'unpublish' : 'publish';
      await fetchWithAuth(`/api/collection-items/manage/${id}/${endpoint}`, { method: 'POST' });
      loadArtifacts();
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
  };
  
  const formatCurrency = (value) => {
    if (!value) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };
  
  const getStatusColor = (status) => {
    const found = AVAILABILITY_STATUSES.find(s => s.value === status);
    return found?.color || '#6b7280';
  };
  
  const getPubStatusColor = (status) => {
    const found = PUBLICATION_STATUSES.find(s => s.value === status);
    return found?.color || '#6b7280';
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 600, color: '#1a1a1a', margin: 0 }}>
            Artifacts & Inventory
          </h1>
          <p style={{ fontSize: '14px', color: '#666', margin: '4px 0 0' }}>
            {total} artifacts in collection
          </p>
        </div>
        <button
          onClick={() => navigate('/owner-dashboard/artifacts/new')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: '#C9A962',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          <Plus size={18} />
          Add Artifact
        </button>
      </div>
      
      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: '1', minWidth: '200px', position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#888' }} />
          <input
            type="text"
            placeholder="Search artifacts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={{
            padding: '10px 12px',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            fontSize: '14px',
            background: 'white',
            minWidth: '150px'
          }}
        >
          <option value="">All Categories</option>
          {CATEGORIES.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '10px 12px',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            fontSize: '14px',
            background: 'white',
            minWidth: '150px'
          }}
        >
          <option value="">All Status</option>
          {PUBLICATION_STATUSES.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>
      
      {/* Error */}
      {error && (
        <div style={{ padding: '12px', background: '#fee2e2', color: '#dc2626', borderRadius: '8px', marginBottom: '16px' }}>
          {error}
        </div>
      )}
      
      {/* Loading */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px', color: '#888' }}>
          Loading artifacts...
        </div>
      ) : artifacts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px', color: '#888' }}>
          <Archive size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p>No artifacts found</p>
          <button
            onClick={() => navigate('/owner-dashboard/artifacts/new')}
            style={{
              marginTop: '16px',
              padding: '10px 20px',
              background: '#C9A962',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Add Your First Artifact
          </button>
        </div>
      ) : (
        <>
          {/* Artifacts Table */}
          <div style={{ 
            background: 'white', 
            border: '1px solid #e8e6e2', 
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9f9f9', borderBottom: '1px solid #e8e6e2' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Artifact</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Category</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Appraised</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Asking</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Published</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {artifacts.map((artifact) => (
                  <tr key={artifact.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ 
                          width: '48px', 
                          height: '48px', 
                          background: '#f5f5f5', 
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden'
                        }}>
                          {artifact.primary_image?.file_path ? (
                            <img 
                              src={`${API_BASE}${artifact.primary_image.file_path}`} 
                              alt="" 
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <Image size={20} color="#ccc" />
                          )}
                        </div>
                        <div>
                          <div style={{ fontWeight: 500, color: '#1a1a1a', fontSize: '14px' }}>
                            {artifact.title}
                          </div>
                          <div style={{ fontSize: '12px', color: '#888' }}>
                            {artifact.inventory_number}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ 
                        padding: '4px 8px', 
                        background: '#f5f5f5', 
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#666'
                      }}>
                        {CATEGORIES.find(c => c.value === artifact.category)?.label || artifact.category}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ 
                        padding: '4px 8px', 
                        background: `${getStatusColor(artifact.availability_status)}20`,
                        color: getStatusColor(artifact.availability_status),
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 500
                      }}>
                        {AVAILABILITY_STATUSES.find(s => s.value === artifact.availability_status)?.label || artifact.availability_status}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: 'monospace', fontSize: '14px' }}>
                      {formatCurrency(artifact.appraised_value_usd)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: 'monospace', fontSize: '14px' }}>
                      {formatCurrency(artifact.asking_price_usd)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <button
                        onClick={() => handlePublish(artifact.id, artifact.publication_status === 'published')}
                        style={{
                          padding: '4px 8px',
                          background: artifact.publication_status === 'published' ? '#dcfce7' : '#fef3c7',
                          color: artifact.publication_status === 'published' ? '#16a34a' : '#d97706',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        {artifact.publication_status === 'published' ? 'Published' : 'Draft'}
                      </button>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => navigate(`/owner-dashboard/artifacts/${artifact.id}`)}
                          style={{ padding: '6px', background: '#f5f5f5', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                          title="Edit"
                        >
                          <Edit size={16} color="#666" />
                        </button>
                        <button
                          onClick={() => window.open(`/collection/items/${artifact.slug}`, '_blank')}
                          style={{ padding: '6px', background: '#f5f5f5', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                          title="View Public Page"
                        >
                          <Eye size={16} color="#666" />
                        </button>
                        <button
                          onClick={() => handleDelete(artifact.id)}
                          style={{ padding: '6px', background: '#fef2f2', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                          title="Archive"
                        >
                          <Trash2 size={16} color="#dc2626" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {total > 25 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
              <button
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  background: 'white',
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                  opacity: page === 1 ? 0.5 : 1
                }}
              >
                Previous
              </button>
              <span style={{ padding: '8px 16px', color: '#666' }}>
                Page {page} of {Math.ceil(total / 25)}
              </span>
              <button
                disabled={page >= Math.ceil(total / 25)}
                onClick={() => setPage(p => p + 1)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  background: 'white',
                  cursor: page >= Math.ceil(total / 25) ? 'not-allowed' : 'pointer',
                  opacity: page >= Math.ceil(total / 25) ? 0.5 : 1
                }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================================
// IMAGE MANAGEMENT SECTION
// ============================================================================

const ImageManagementSection = ({ artifactId, images = [], primaryImage, onUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = React.useRef(null);
  
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };
  
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      await uploadFiles(Array.from(files));
    }
  };
  
  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await uploadFiles(Array.from(files));
    }
    // Reset input
    e.target.value = '';
  };
  
  const uploadFiles = async (files) => {
    setUploading(true);
    setUploadProgress(0);
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('role', images.length === 0 ? 'primary' : 'detail');
        formData.append('is_public', 'true');
        
        const response = await fetch(`${API_BASE}/api/collection-items/manage/${artifactId}/images/upload`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
        
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || 'Upload failed');
        }
        
        setUploadProgress(((i + 1) / files.length) * 100);
      } catch (err) {
        alert(`Failed to upload ${file.name}: ${err.message}`);
      }
    }
    
    setUploading(false);
    setUploadProgress(0);
    onUpdate?.();
  };
  
  const handleDelete = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/collection-items/manage/${artifactId}/images/${imageId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Delete failed');
      }
      
      onUpdate?.();
    } catch (err) {
      alert('Failed to delete image: ' + err.message);
    }
  };
  
  const handleSetPrimary = async (imageId) => {
    try {
      const response = await fetch(`${API_BASE}/api/collection-items/manage/${artifactId}/images/${imageId}/primary`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Update failed');
      }
      
      onUpdate?.();
    } catch (err) {
      alert('Failed to set primary image: ' + err.message);
    }
  };
  
  const sectionStyle = {
    background: 'white',
    border: '1px solid #e8e6e2',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px'
  };
  
  const sectionTitleStyle = {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '1px solid #f0f0f0'
  };
  
  return (
    <div style={sectionStyle}>
      <h2 style={sectionTitleStyle}>Images</h2>
      
      {/* Upload Area */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragActive ? '#C9A962' : '#e0e0e0'}`,
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragActive ? 'rgba(201, 169, 98, 0.05)' : '#fafafa',
          transition: 'all 0.2s',
          marginBottom: images.length > 0 ? '20px' : 0
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        {uploading ? (
          <div>
            <div style={{ width: '100%', height: '8px', background: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ width: `${uploadProgress}%`, height: '100%', background: '#C9A962', transition: 'width 0.3s' }} />
            </div>
            <p style={{ marginTop: '12px', color: '#666', fontSize: '14px' }}>Uploading... {Math.round(uploadProgress)}%</p>
          </div>
        ) : (
          <>
            <Image size={32} style={{ color: '#888', marginBottom: '12px' }} />
            <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
              Drop images here or click to upload
            </p>
            <p style={{ color: '#999', fontSize: '12px', marginTop: '4px' }}>
              Supports JPG, PNG, GIF, WebP (max 25MB each)
            </p>
          </>
        )}
      </div>
      
      {/* Image Grid */}
      {images.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '16px' }}>
          {images.map((img, index) => {
            const isPrimary = primaryImage?.id === img.id || img.role === 'primary';
            
            return (
              <div
                key={img.id}
                style={{
                  position: 'relative',
                  background: '#f5f5f5',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: isPrimary ? '2px solid #C9A962' : '1px solid #e0e0e0'
                }}
              >
                <div style={{ aspectRatio: '1', overflow: 'hidden' }}>
                  <img
                    src={`${API_BASE}${img.file_path}`}
                    alt={img.alt_text || `Image ${index + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                
                {/* Primary Badge */}
                {isPrimary && (
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    left: '8px',
                    padding: '2px 6px',
                    background: '#C9A962',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: 600,
                    borderRadius: '4px'
                  }}>
                    PRIMARY
                  </div>
                )}
                
                {/* Actions */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: '8px',
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                  display: 'flex',
                  gap: '4px',
                  justifyContent: 'flex-end'
                }}>
                  {!isPrimary && (
                    <button
                      onClick={() => handleSetPrimary(img.id)}
                      style={{
                        padding: '4px 8px',
                        background: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '10px',
                        cursor: 'pointer'
                      }}
                      title="Set as Primary"
                    >
                      Set Primary
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(img.id)}
                    style={{
                      padding: '4px 8px',
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '10px',
                      cursor: 'pointer'
                    }}
                    title="Delete"
                  >
                    <X size={12} />
                  </button>
                </div>
                
                {/* Caption */}
                {img.caption && (
                  <div style={{ padding: '8px', fontSize: '12px', color: '#666' }}>
                    {img.caption}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ARTIFACT EDITOR
// ============================================================================

export const ArtifactEditorSection = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    category: 'other',
    relationship: 'other',
    short_summary: '',
    full_description: '',
    historical_background: '',
    public_provenance: '',
    private_provenance: '',
    date_or_period: '',
    condition_summary: '',
    private_condition_notes: '',
    dimensions: '',
    materials: '',
    certification: '',
    appraised_value_usd: '',
    asking_price_usd: '',
    minimum_acceptable_price: '',
    target_sale_price: '',
    show_appraised_value: true,
    availability_status: 'not_currently_available',
    // Commercial workflow fields
    commercial_status: 'draft',
    show_asking_price: false,
    public_sale_status: '',
    asking_price: '',
    asking_price_currency: 'USD',
    private_financial_notes: '',
    keywords: '',
    seo_title: '',
    meta_description: '',
    private_notes: '',
    storage_location: '',
    insurance_value: ''
  });
  
  useEffect(() => {
    if (!isNew && id) {
      loadArtifact();
    }
  }, [id, isNew]);
  
  const loadArtifact = async () => {
    try {
      setLoading(true);
      const data = await fetchWithAuth(`/api/collection-items/manage/${id}`);
      setFormData({
        ...data,
        keywords: Array.isArray(data.keywords) ? data.keywords.join(', ') : '',
        appraised_value_usd: data.appraised_value_usd || '',
        asking_price_usd: data.asking_price_usd || '',
        minimum_acceptable_price: data.minimum_acceptable_price || '',
        target_sale_price: data.target_sale_price || '',
        insurance_value: data.insurance_value || '',
        // Commercial workflow fields
        commercial_status: data.commercial_status || 'draft',
        show_asking_price: data.show_asking_price || false,
        public_sale_status: data.public_sale_status || '',
        asking_price: data.asking_price || '',
        asking_price_currency: data.asking_price_currency || 'USD',
        private_financial_notes: data.private_financial_notes || ''
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      
      // Prepare data
      const submitData = {
        ...formData,
        keywords: formData.keywords ? formData.keywords.split(',').map(k => k.trim()).filter(Boolean) : [],
        appraised_value_usd: formData.appraised_value_usd ? parseFloat(formData.appraised_value_usd) : null,
        asking_price_usd: formData.asking_price_usd ? parseFloat(formData.asking_price_usd) : null,
        minimum_acceptable_price: formData.minimum_acceptable_price ? parseFloat(formData.minimum_acceptable_price) : null,
        target_sale_price: formData.target_sale_price ? parseFloat(formData.target_sale_price) : null,
        insurance_value: formData.insurance_value ? parseFloat(formData.insurance_value) : null,
        // Commercial workflow fields
        asking_price: formData.asking_price ? parseFloat(formData.asking_price) : null
      };
      
      if (isNew) {
        const result = await fetchWithAuth('/api/collection-items/manage/create', {
          method: 'POST',
          body: JSON.stringify(submitData)
        });
        navigate(`/owner-dashboard/artifacts/${result.id}`);
      } else {
        await fetchWithAuth(`/api/collection-items/manage/${id}`, {
          method: 'PUT',
          body: JSON.stringify(submitData)
        });
        loadArtifact();
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: '#888' }}>
        Loading artifact...
      </div>
    );
  }

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px'
  };
  
  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: '#333',
    marginBottom: '6px'
  };
  
  const sectionStyle = {
    background: 'white',
    border: '1px solid #e8e6e2',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px'
  };
  
  const sectionTitleStyle = {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '1px solid #f0f0f0'
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1000px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/owner-dashboard/artifacts')}
          style={{ padding: '8px', background: '#f5f5f5', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
        >
          <ChevronRight size={20} style={{ transform: 'rotate(180deg)' }} />
        </button>
        <h1 style={{ fontSize: '24px', fontWeight: 600, color: '#1a1a1a', margin: 0 }}>
          {isNew ? 'Add New Artifact' : 'Edit Artifact'}
        </h1>
      </div>
      
      {error && (
        <div style={{ padding: '12px', background: '#fee2e2', color: '#dc2626', borderRadius: '8px', marginBottom: '24px' }}>
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Basic Information</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                style={inputStyle}
                placeholder="e.g., Original Aloha from Hawaii Concert Ticket"
              />
            </div>
            <div>
              <label style={labelStyle}>Category</label>
              <select name="category" value={formData.category} onChange={handleChange} style={inputStyle}>
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Relationship to Collection</label>
              <select name="relationship" value={formData.relationship} onChange={handleChange} style={inputStyle}>
                {RELATIONSHIPS.map(rel => (
                  <option key={rel.value} value={rel.value}>{rel.label}</option>
                ))}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Short Summary</label>
              <textarea
                name="short_summary"
                value={formData.short_summary || ''}
                onChange={handleChange}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="Brief description for listings and cards"
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Full Description</label>
              <textarea
                name="full_description"
                value={formData.full_description || ''}
                onChange={handleChange}
                rows={4}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="Detailed description of the artifact"
              />
            </div>
          </div>
        </div>
        
        {/* Historical & Provenance */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Historical Information & Provenance</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Historical Background</label>
              <textarea
                name="historical_background"
                value={formData.historical_background || ''}
                onChange={handleChange}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="Historical context and significance"
              />
            </div>
            <div>
              <label style={labelStyle}>Public Provenance</label>
              <textarea
                name="public_provenance"
                value={formData.public_provenance || ''}
                onChange={handleChange}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="Provenance visible to the public"
              />
            </div>
            <div>
              <label style={labelStyle}>Private Provenance (Owner Only)</label>
              <textarea
                name="private_provenance"
                value={formData.private_provenance || ''}
                onChange={handleChange}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical', background: '#fef9e7' }}
                placeholder="Private provenance details - not shown publicly"
              />
            </div>
            <div>
              <label style={labelStyle}>Date or Period</label>
              <input
                type="text"
                name="date_or_period"
                value={formData.date_or_period || ''}
                onChange={handleChange}
                style={inputStyle}
                placeholder="e.g., January 14, 1973"
              />
            </div>
          </div>
        </div>
        
        {/* Physical Details */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Physical Details</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Condition Summary</label>
              <textarea
                name="condition_summary"
                value={formData.condition_summary || ''}
                onChange={handleChange}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="Public condition description"
              />
            </div>
            <div>
              <label style={labelStyle}>Private Condition Notes (Owner Only)</label>
              <textarea
                name="private_condition_notes"
                value={formData.private_condition_notes || ''}
                onChange={handleChange}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical', background: '#fef9e7' }}
                placeholder="Private condition concerns"
              />
            </div>
            <div>
              <label style={labelStyle}>Dimensions</label>
              <input
                type="text"
                name="dimensions"
                value={formData.dimensions || ''}
                onChange={handleChange}
                style={inputStyle}
                placeholder="e.g., 3.5 x 2.5 inches"
              />
            </div>
            <div>
              <label style={labelStyle}>Materials</label>
              <input
                type="text"
                name="materials"
                value={formData.materials || ''}
                onChange={handleChange}
                style={inputStyle}
                placeholder="e.g., Printed cardstock"
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Certification/Grading</label>
              <input
                type="text"
                name="certification"
                value={formData.certification || ''}
                onChange={handleChange}
                style={inputStyle}
                placeholder="e.g., PSA VG-EX 4"
              />
            </div>
          </div>
        </div>
        
        {/* Valuation & Pricing */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Valuation & Pricing</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Appraised Value (USD)</label>
              <input
                type="number"
                name="appraised_value_usd"
                value={formData.appraised_value_usd}
                onChange={handleChange}
                style={inputStyle}
                placeholder="25000"
                step="0.01"
              />
            </div>
            <div>
              <label style={labelStyle}>Asking Price (USD)</label>
              <input
                type="number"
                name="asking_price_usd"
                value={formData.asking_price_usd}
                onChange={handleChange}
                style={{ ...inputStyle, background: '#fef9e7' }}
                placeholder="30000"
                step="0.01"
              />
            </div>
            <div>
              <label style={labelStyle}>Minimum Acceptable Price (USD)</label>
              <input
                type="number"
                name="minimum_acceptable_price"
                value={formData.minimum_acceptable_price}
                onChange={handleChange}
                style={{ ...inputStyle, background: '#fef9e7' }}
                placeholder="24000"
                step="0.01"
              />
            </div>
            <div>
              <label style={labelStyle}>Target Sale Price (USD)</label>
              <input
                type="number"
                name="target_sale_price"
                value={formData.target_sale_price}
                onChange={handleChange}
                style={{ ...inputStyle, background: '#fef9e7' }}
                placeholder="28000"
                step="0.01"
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                name="show_appraised_value"
                checked={formData.show_appraised_value}
                onChange={handleChange}
                id="show_appraised_value"
              />
              <label htmlFor="show_appraised_value" style={{ fontSize: '14px' }}>
                Show appraised value publicly
              </label>
            </div>
          </div>
        </div>
        
        {/* Availability */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Availability Status</h2>
          <div>
            <label style={labelStyle}>Status</label>
            <select 
              name="availability_status" 
              value={formData.availability_status} 
              onChange={handleChange}
              style={inputStyle}
            >
              {AVAILABILITY_STATUSES.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Commercial Workflow - PRIVATE */}
        <div style={{ ...sectionStyle, background: '#fef9e7', border: '2px solid #f59e0b' }}>
          <h2 style={{ ...sectionTitleStyle, color: '#92400e' }}>
            Commercial Workflow
            <span style={{ fontSize: '12px', fontWeight: 'normal', marginLeft: '8px', color: '#a16207' }}>
              (Private - Never shown publicly)
            </span>
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Commercial Status</label>
              <select 
                name="commercial_status" 
                value={formData.commercial_status || 'draft'} 
                onChange={handleChange}
                style={{ ...inputStyle, background: 'white' }}
              >
                <option value="draft">Draft - Not ready for sale</option>
                <option value="available_for_private_sale">Available for Private Sale</option>
                <option value="reserved">Reserved - Held for buyer</option>
                <option value="sold">Sold - Completed</option>
                <option value="withdrawn">Withdrawn - Removed from sale</option>
                <option value="not_for_sale">Not for Sale - Permanent collection</option>
              </select>
              <p style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                Internal status (separate from public availability)
              </p>
            </div>
            <div>
              <label style={labelStyle}>Public Sale Status Label</label>
              <input
                type="text"
                name="public_sale_status"
                value={formData.public_sale_status || ''}
                onChange={handleChange}
                style={{ ...inputStyle, background: 'white' }}
                placeholder="e.g., Inquiries Welcome, Price on Request"
              />
              <p style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                What the public sees (leave blank for default)
              </p>
            </div>
            <div>
              <label style={labelStyle}>Asking Price</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="number"
                  name="asking_price"
                  value={formData.asking_price || ''}
                  onChange={handleChange}
                  style={{ ...inputStyle, background: 'white', flex: 1 }}
                  placeholder="30000"
                  step="0.01"
                />
                <select 
                  name="asking_price_currency" 
                  value={formData.asking_price_currency || 'USD'} 
                  onChange={handleChange}
                  style={{ ...inputStyle, background: 'white', width: '80px' }}
                >
                  <option value="USD">USD</option>
                  <option value="JPY">JPY</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '24px' }}>
              <input
                type="checkbox"
                name="show_asking_price"
                checked={formData.show_asking_price || false}
                onChange={handleChange}
                id="show_asking_price"
              />
              <label htmlFor="show_asking_price" style={{ fontSize: '14px' }}>
                Show asking price publicly
              </label>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Private Financial Notes</label>
              <textarea
                name="private_financial_notes"
                value={formData.private_financial_notes || ''}
                onChange={handleChange}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical', background: 'white' }}
                placeholder="Sensitive financial commentary, negotiation notes..."
              />
            </div>
          </div>
        </div>
        
        {/* SEO */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>SEO & Discovery</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Keywords (comma separated)</label>
              <input
                type="text"
                name="keywords"
                value={formData.keywords}
                onChange={handleChange}
                style={inputStyle}
                placeholder="elvis presley, aloha from hawaii, 1973 concert ticket"
              />
            </div>
            <div>
              <label style={labelStyle}>SEO Title</label>
              <input
                type="text"
                name="seo_title"
                value={formData.seo_title || ''}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Custom page title for search engines"
              />
            </div>
            <div>
              <label style={labelStyle}>Meta Description</label>
              <textarea
                name="meta_description"
                value={formData.meta_description || ''}
                onChange={handleChange}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="Description shown in search results (150-160 characters recommended)"
              />
            </div>
          </div>
        </div>
        
        {/* Private Notes */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Private Owner Notes</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Private Notes (Owner Only)</label>
              <textarea
                name="private_notes"
                value={formData.private_notes || ''}
                onChange={handleChange}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', background: '#fef9e7' }}
                placeholder="Internal notes, reminders, references..."
              />
            </div>
            <div>
              <label style={labelStyle}>Storage Location</label>
              <input
                type="text"
                name="storage_location"
                value={formData.storage_location || ''}
                onChange={handleChange}
                style={{ ...inputStyle, background: '#fef9e7' }}
                placeholder="e.g., Safe box A-12"
              />
            </div>
            <div>
              <label style={labelStyle}>Insurance Value (USD)</label>
              <input
                type="number"
                name="insurance_value"
                value={formData.insurance_value}
                onChange={handleChange}
                style={{ ...inputStyle, background: '#fef9e7' }}
                placeholder="35000"
                step="0.01"
              />
            </div>
          </div>
        </div>
        
        {/* Images Section - Only show for existing artifacts */}
        {!isNew && formData.id && (
          <ImageManagementSection 
            artifactId={formData.id} 
            images={formData.images || []}
            primaryImage={formData.primary_image}
            onUpdate={loadArtifact}
          />
        )}
        
        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => navigate('/owner-dashboard/artifacts')}
            style={{
              padding: '12px 24px',
              background: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '12px 24px',
              background: '#C9A962',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1
            }}
          >
            {saving ? 'Saving...' : (isNew ? 'Create Artifact' : 'Save Changes')}
          </button>
        </div>
      </form>
    </div>
  );
};

// ============================================================================
// FINANCIAL DASHBOARD
// ============================================================================

// ============================================================================
// INVENTORY DASHBOARD (USES NEW COMPREHENSIVE COMPONENT)
// ============================================================================

// Import the new comprehensive inventory dashboard
import InventoryDashboardComponent from './InventoryDashboard';

export const InventoryDashboardSection = InventoryDashboardComponent;

// ============================================================================
// INQUIRIES SECTION
// ============================================================================

export const InquiriesSection = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [newCount, setNewCount] = useState(0);
  
  useEffect(() => {
    loadInquiries();
  }, []);
  
  const loadInquiries = async () => {
    try {
      setLoading(true);
      const data = await fetchWithAuth('/api/collection-items/manage/inquiries/list');
      setInquiries(data.inquiries || []);
      setTotal(data.total || 0);
      setNewCount(data.new_count || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const updateStatus = async (id, newStatus) => {
    try {
      await fetchWithAuth(`/api/collection-items/manage/inquiries/${id}/status?new_status=${newStatus}`, {
        method: 'PUT'
      });
      loadInquiries();
    } catch (err) {
      alert('Failed to update status: ' + err.message);
    }
  };
  
  const statusColors = {
    new: '#3b82f6',
    contacted: '#22c55e',
    follow_up_required: '#f59e0b',
    offer_received: '#8b5cf6',
    negotiating: '#ec4899',
    closed: '#6b7280',
    spam: '#ef4444'
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 600, color: '#1a1a1a', margin: 0 }}>
            Inquiries
          </h1>
          <p style={{ fontSize: '14px', color: '#666', margin: '4px 0 0' }}>
            {total} total, {newCount} new
          </p>
        </div>
      </div>
      
      {error && (
        <div style={{ padding: '12px', background: '#fee2e2', color: '#dc2626', borderRadius: '8px', marginBottom: '16px' }}>
          {error}
        </div>
      )}
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px', color: '#888' }}>
          Loading inquiries...
        </div>
      ) : inquiries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px', color: '#888', background: 'white', borderRadius: '12px', border: '1px solid #e8e6e2' }}>
          No inquiries yet
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {inquiries.map((inquiry, idx) => (
            <div key={inquiry.id || `inquiry-${idx}`} style={{
              background: 'white',
              border: '1px solid #e8e6e2',
              borderRadius: '12px',
              padding: '20px',
              borderLeft: `4px solid ${statusColors[inquiry.status] || '#6b7280'}`
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    background: `${statusColors[inquiry.status]}20`,
                    color: statusColors[inquiry.status],
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    marginBottom: '8px'
                  }}>
                    {inquiry.status?.replace(/_/g, ' ')}
                  </span>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>
                    {inquiry.artifact_title}
                  </h3>
                  <p style={{ fontSize: '12px', color: '#888', margin: '4px 0 0' }}>
                    {inquiry.artifact_inventory_number}
                  </p>
                </div>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  {new Date(inquiry.submitted_at).toLocaleDateString()}
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px', fontSize: '14px' }}>
                <div>
                  <strong>From:</strong> {inquiry.name} ({inquiry.email})
                </div>
                <div>
                  <strong>Type:</strong> {inquiry.inquiry_type?.replace(/_/g, ' ')}
                </div>
              </div>
              
              <div style={{ background: '#f9f9f9', padding: '12px', borderRadius: '8px', marginBottom: '12px', fontSize: '14px' }}>
                {inquiry.message}
              </div>
              
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {inquiry.status === 'new' && (
                  <button
                    onClick={() => updateStatus(inquiry.id, 'contacted')}
                    style={{ padding: '6px 12px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                  >
                    Mark Contacted
                  </button>
                )}
                {inquiry.status !== 'closed' && inquiry.status !== 'spam' && (
                  <>
                    <button
                      onClick={() => updateStatus(inquiry.id, 'follow_up_required')}
                      style={{ padding: '6px 12px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      Follow-up Required
                    </button>
                    <button
                      onClick={() => updateStatus(inquiry.id, 'closed')}
                      style={{ padding: '6px 12px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      Close
                    </button>
                    <button
                      onClick={() => updateStatus(inquiry.id, 'spam')}
                      style={{ padding: '6px 12px', background: 'white', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      Mark Spam
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ArtifactsListSection;
